import React, { useState, useEffect, useCallback, useMemo, useRef, useLayoutEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Alert,
  CircularProgress,
  Grid,
  Chip,
  Divider,
  LinearProgress,
  Stack
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ArrowForwardRounded as ArrowForwardRoundedIcon,
  CheckCircleRounded as CheckCircleRoundedIcon,
  RadioButtonUncheckedRounded as RadioButtonUncheckedRoundedIcon,
  TaskAltRounded as TaskAltRoundedIcon,
  TipsAndUpdatesRounded as TipsAndUpdatesRoundedIcon,
} from '@mui/icons-material';
import PatientDetailsStep from './steps/PatientDetailsStep';
import CharacterSelectionStep from './steps/CharacterSelectionStep';
import PatientNarrativeComposer from './steps/PatientNarrativeComposer';
import CaseContentStep from './steps/CaseContentStep';
import QuestionsStep from './steps/QuestionsStep';
import LearningStep from './steps/LearningStep';
import api from '../../services/api';
import { getCurrentUser } from '../../services/auth';
import { defaultAnimationProfile, normalizeAnimationProfile } from '../../utils/animationProfile';
import { isGlobalAdmin, isTenantScopedAdmin } from '../../utils/role';
import { isoToDisplay, displayToISO, isoToLocalDateTime, localDateTimeToISO } from '../../utils/date';
import { scrollPageToTop } from '../../utils/scroll';
import CaseCreatorGuidelines from './CaseCreatorGuidelines';
import CaseVersionTimeline from './CaseVersionTimeline';
import {
  caseManagerHeroSx,
  caseManagerPageSx,
  caseManagerPalette,
  caseManagerSurfaceSx,
  CaseManagerHeroSymbol,
  publicBodyFontFamily,
  publicHeadingFontFamily,
  publicPrimaryButtonSx,
  publicSecondaryButtonSx,
} from './caseManagerStyles';

const steps = [
  'Case/Patient Details',
  'Character Selection',
  'Patient Narrative',
  'Mark Scheme',
  'Questions',
  'Learning Resources'
];
const requiredStepIndexes = [0, 1, 2, 3];
const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

const isTenantCaseManagerUser = (user) => {
  const accountType = (user?.accountType || '').toString().trim().toLowerCase();
  return (
    isTenantScopedAdmin(user)
    && Boolean(user?.universityId || user?.tenantId || user?.tenant?._id || accountType === 'university')
  );
};

const isCommunityCaseOrigin = (provenance = {}) => {
  const originType = (provenance.originType || '').toString().trim().toLowerCase();
  if (originType.includes('community')) {
    return true;
  }

  return Boolean(provenance.sourceCaseId && originType !== 'virtual-medic');
};

const scoringFields = [
  {
    key: 'historyQuestions',
    label: 'History Questions',
    description: 'Marks awarded for structured history-taking prompts.'
  },
  {
    key: 'flow',
    label: 'Flow',
    description: 'Delivery, sequencing, and communication flow.'
  },
  {
    key: 'examinerQuestions',
    label: 'Examiner Questions',
    description: 'Performance on examiner-led questions.'
  },
  {
    key: 'empathy',
    label: 'Empathy',
    description: 'Empathy, rapport, and patient-centred communication.'
  }
];

const CaseEditor = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);

  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [guidelinesOpen, setGuidelinesOpen] = useState(false);
  const hasMountedStepRef = useRef(false);
  const currentUser = useMemo(() => getCurrentUser(), []);
  const allowNewSpecialty = false;
  const allowPublicTemplates = useMemo(
    () => isGlobalAdmin(currentUser) || isTenantScopedAdmin(currentUser),
    [currentUser]
  );

  // Form data
  const [caseData, setCaseData] = useState({
    title: '',
    presentingComplaint: '',
    specialty: '',
    tags: [],
    patientName: '',
    gender: '',
    dateOfBirth: '',
    age: '',
    difficulty: '',
    shortDescription: '',
    patientNarrative: '',
    examinationFindings: '',
    characterId: '',
    openAIVoiceModel: 'ash',
    geminiVoiceModel: 'Kore',
    characterPersonality: '',
    characterPersonalityCustom: '',
    characterAccent: '',
    characterAccentCustom: '',
    characterSpeechStyle: '',
    animationProfile: { ...defaultAnimationProfile },
    sections: [],
    examinerQuestions: [],
    learning: {
      summary: '',
      links: [],
      attachments: [],
    },
    visibility: {
      scheduling: {
        alwaysAvailable: true,
        startsAt: '',
        endsAt: '',
        timezone: detectedTimezone,
      },
      cohort: {
        allowAllGroups: true,
        allowedGroupIds: [],
        allowedYearLevels: [],
      },
      accessNotes: '',
      publication: {
        state: 'draft',
        updatedAt: null,
        updatedBy: null,
        reason: '',
      },
    },
    provenance: {
      originType: 'virtual-medic',
      originTenantId: null,
      originPackId: null,
      originCaseId: null,
      sourceCaseId: null,
      originNotes: '',
      lastSyncedFromOriginAt: null,
      lastOriginVersion: null,
    },
  });
  const [scoringDefaults, setScoringDefaults] = useState(null);
  const [scoringConfig, setScoringConfig] = useState(null);
  const initialScoringOverridesRef = useRef(null);
  const showVisibilityControls = useMemo(
    () => isTenantCaseManagerUser(currentUser) && !isCommunityCaseOrigin(caseData.provenance),
    [currentUser, caseData.provenance]
  );
  const scoringTotal = useMemo(() => {
    if (!scoringConfig || !scoringConfig.resolved) {
      return 0;
    }
    return scoringFields.reduce((sum, field) => (
      sum + Number(scoringConfig.resolved[field.key] || 0)
    ), 0);
  }, [scoringConfig]);
  const scoringIsBalanced = useMemo(() => Math.abs(scoringTotal - 1) < 0.001, [scoringTotal]);

  // Presets data
  const [presets, setPresets] = useState({
    presentingComplaints: [],
    presentingComplaintGroups: { items: [], ownerType: null, ownerId: null },
    specialties: [],
    specialtyGroups: { items: [], ownerType: null, ownerId: null },
    tags: [],
    characters: [],
    voiceModels: [],
    geminiVoiceModels: [],
    groups: [],
  });

  const normalizeScoringPayload = useCallback((payload, fallbackDefaults = null) => {
    if (!payload || typeof payload !== 'object') {
      const defaults = fallbackDefaults || {};
      return {
        defaults,
        resolved: { ...defaults },
        overrides: null,
        source: 'global-defaults'
      };
    }

    const defaults = payload.defaults && typeof payload.defaults === 'object'
      ? payload.defaults
      : (fallbackDefaults || {});
    const resolved = payload.resolved && typeof payload.resolved === 'object'
      ? payload.resolved
      : { ...defaults };
    const overrides = payload.overrides && Object.keys(payload.overrides).length ? payload.overrides : null;
    const source = payload.source || (overrides ? 'case-overrides' : 'global-defaults');

    return {
      defaults,
      resolved,
      overrides,
      source
    };
  }, []);

useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return 'You have unsaved changes. Are you sure you want to leave?';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  useEffect(() => {
    const handlePopState = () => {
      if (hasUnsavedChanges) {
        const shouldLeave = window.confirm(
          'You have unsaved changes. Are you sure you want to leave? All changes will be lost.'
        );
        if (!shouldLeave) {
          window.history.pushState(null, '', window.location.pathname);
        }
      }
    };

    window.addEventListener('popstate', handlePopState);

    if (hasUnsavedChanges) {
      window.history.pushState(null, '', window.location.pathname);
    }

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [hasUnsavedChanges]);

  useLayoutEffect(() => {
    if (!hasMountedStepRef.current) {
      hasMountedStepRef.current = true;
      return;
    }

    scrollPageToTop();
  }, [currentStep]);

  const loadScoringDefaults = useCallback(async () => {
    try {
      const response = await api.get('/cases/meta/scoring-config');
      const normalized = normalizeScoringPayload(response.data);
      setScoringDefaults(normalized);
      if (!isEditing) {
        setScoringConfig(normalized);
        initialScoringOverridesRef.current = null;
      }
    } catch (err) {
      console.error('Error loading scoring defaults:', err);
      const fallback = normalizeScoringPayload(null);
      setScoringDefaults(fallback);
      if (!isEditing) {
        setScoringConfig(fallback);
      }
    }
  }, [isEditing, normalizeScoringPayload]);

  useEffect(() => {
    loadPresets();
    loadScoringDefaults().then(() => {
      if (isEditing) {
        loadCase();
      }
    });
  }, [isEditing, id, loadScoringDefaults]);

  const loadPresets = async () => {
    try {
      const [complaintsRes, specialtiesRes, charactersRes, voicesRes, geminiVoicesRes, groupsRes] = await Promise.all([
        api.get('/platform/presets/presenting-complaints'),
        api.get('/platform/presets/specialties'),
        api.get('/platform/characters'),
        api.get('/platform/voices'),
        api.get('/platform/voices/gemini'),
        api.get('/groups')
      ]);

      const normalizePresetPayload = (payload) => {
        const source = payload || {};
        const items = Array.isArray(source.items)
          ? source.items
          : Array.isArray(source.all)
            ? source.all
            : Array.isArray(source)
              ? source
              : [];

        const ownerType = source.ownerType || (source.ownerId ? 'university' : 'virtual-medic');
        const ownerId = source.ownerId ?? null;

        return {
          items: items.map((item) => ({
            ...item,
            ownerType: item.ownerType || ownerType,
            ownerId: item.ownerId ?? ownerId
          })),
          ownerType,
          ownerId
        };
      };

      const complaintPayload = normalizePresetPayload(complaintsRes.data);
      const specialtyPayload = normalizePresetPayload(specialtiesRes.data);

      setPresets({
        presentingComplaints: complaintPayload.items,
        presentingComplaintGroups: complaintPayload,
        specialties: specialtyPayload.items,
        specialtyGroups: specialtyPayload,
        characters: charactersRes.data,
        voiceModels: voicesRes.data,
        geminiVoiceModels: geminiVoicesRes.data,
        groups: Array.isArray(groupsRes.data) ? groupsRes.data : []
      });
    } catch (err) {
      console.error('Error loading presets:', err);
      setError('Failed to load form options');
    }
  };

  const loadCase = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/cases/${id}`);
      const case_data = response.data;

      const normalizedConfig = normalizeScoringPayload(
        case_data.scoringConfiguration,
        scoringDefaults?.defaults || null
      );

      const visibility = case_data.visibility || {};
      const scheduling = visibility.scheduling || {};
      const cohort = visibility.cohort || {};

      const normalizedVisibility = {
        scheduling: {
          alwaysAvailable: scheduling.alwaysAvailable !== false,
          startsAt: scheduling.startsAt ? isoToLocalDateTime(scheduling.startsAt) : '',
          endsAt: scheduling.endsAt ? isoToLocalDateTime(scheduling.endsAt) : '',
          timezone: detectedTimezone,
        },
        cohort: {
          allowAllGroups: cohort.allowAllGroups !== false,
          allowedGroupIds: Array.isArray(cohort.allowedGroupIds)
            ? Array.from(new Set(cohort.allowedGroupIds.map((value) => {
              if (!value) return null;
              if (typeof value === 'string') return value;
              if (value._id) return value._id.toString();
              return value.toString();
            }).filter(Boolean)))
            : [],
          allowedYearLevels: Array.isArray(cohort.allowedYearLevels)
            ? Array.from(new Set(
              cohort.allowedYearLevels
                .map((level) => (level === null || level === undefined
                  ? null
                  : level.toString().trim().toLowerCase()))
                .filter(Boolean)
            ))
            : [],
        },
        accessNotes: visibility.accessNotes || '',
        publication: {
          state: visibility.publication?.state || 'draft',
          updatedAt: visibility.publication?.updatedAt || null,
          updatedBy: visibility.publication?.updatedBy || null,
          reason: visibility.publication?.reason || '',
        },
      };

      const provenance = case_data.provenance || {};
      const resolvedOpenAIVoiceModel = case_data.openAIVoiceModel || case_data.voiceModel || 'ash';
      const resolvedGeminiVoiceModel = case_data.geminiVoiceModel || 'Kore';

      setCaseData({
        title: case_data.title,
        presentingComplaint: case_data.presentingComplaint,
        specialty: case_data.specialty,
        tags: Array.isArray(case_data.tags) ? case_data.tags : [],
        patientName: case_data.patientName,
        gender: case_data.gender,
        dateOfBirth: isoToDisplay(case_data.dateOfBirth),
        age: case_data.age != null ? case_data.age.toString() : '',
        difficulty: case_data.difficulty,
        shortDescription: case_data.shortDescription || '',
        patientNarrative: (case_data.patientNarrative || '').trim(),
        examinationFindings: (case_data.examinationFindings || '').trim(),
        characterId: case_data.characterId?._id || '',
        openAIVoiceModel: resolvedOpenAIVoiceModel,
        geminiVoiceModel: resolvedGeminiVoiceModel,
        characterPersonality: case_data.characterPersonality || '',
        characterPersonalityCustom: case_data.characterPersonalityCustom || '',
        characterAccent: case_data.characterAccent || '',
        characterAccentCustom: case_data.characterAccentCustom || '',
        characterSpeechStyle: case_data.characterSpeechStyle || '',
        animationProfile: normalizeAnimationProfile(case_data.animationProfile),
        environments: case_data.environments,
        sections: (case_data.sections || []).map(section => ({
          id: section._id,
          title: section.title,
          sectionType: section.sectionType,
          order: section.order,
          isCustom: section.isCustom,
          items: (section.items || []).map(item => ({
            id: item._id,
            itemId: item.itemId,
            title: item.title,
            maxMarks: item.maxMarks,
            markingCriteria: item.markingCriteria,
            extendedInstructions: item.extendedInstructions || '',
            patientResponse: item.patientResponse,
            domainCodes: item.domainCodes || [],
            competencyCodes: item.competencyCodes || [],
            safetyCritical: Boolean(item.safetyCritical),
            omissionEligible: item.omissionEligible !== false,
            taxonomyVersion: item.taxonomyVersion || '1',
            order: item.order
          }))
        })),
        examinerQuestions: (case_data.examinerQuestions || []).map(q => ({
          id: q._id,
          question: q.question,
          correctAnswer: q.correctAnswer,
          maxMarks: q.maxMarks,
          markingInstructions: q.markingInstructions,
          domainCodes: q.domainCodes || [],
          competencyCodes: q.competencyCodes || [],
          safetyCritical: Boolean(q.safetyCritical),
          taxonomyVersion: q.taxonomyVersion || '1',
          mediaFiles: q.mediaFiles || [],
          order: q.order
        })),
        learning: {
          summary: case_data.learning?.summary || '',
          links: Array.isArray(case_data.learning?.links)
            ? case_data.learning.links.map((link, index) => ({
              id: link.id || link._id || `learning_link_${index}`,
              label: link.label || link.title || '',
              url: link.url || ''
            })).filter((link) => link.label && link.url)
            : [],
          attachments: Array.isArray(case_data.learning?.attachments) ? case_data.learning.attachments : []
        },
        visibility: normalizedVisibility,
        provenance: {
          originType: provenance.originType || 'virtual-medic',
          originTenantId: provenance.originTenantId || null,
          originPackId: provenance.originPackId || null,
          originCaseId: provenance.originCaseId || null,
          sourceCaseId: provenance.sourceCaseId || case_data.sourceCaseId || null,
          originNotes: provenance.originNotes || '',
          lastSyncedFromOriginAt: provenance.lastSyncedFromOriginAt || null,
          lastOriginVersion: provenance.lastOriginVersion || null,
        },
      });
      setScoringConfig(normalizedConfig);
      initialScoringOverridesRef.current = normalizedConfig.overrides;
      setHasUnsavedChanges(false);
    } catch (err) {
      console.error('Error loading case:', err);
      setError('Failed to load case data');
    } finally {
      setLoading(false);
    }
  };

  const updateCaseData = (updates) => {
    setCaseData(prev => ({ ...prev, ...updates }));
    setValidationErrors([]); // Clear validation errors when data changes
    setHasUnsavedChanges(true);
  };

  const handleScoringWeightChange = useCallback((key, nextPercent) => {
    setScoringConfig(prev => {
      if (!prev) {
        return prev;
      }
      const percentValue = Number.isFinite(nextPercent) ? nextPercent : 0;
      const clampedPercent = Math.max(0, Math.min(100, percentValue));
      const decimalValue = parseFloat((clampedPercent / 100).toFixed(4));

      const nextResolved = {
        ...prev.resolved,
        [key]: decimalValue
      };

      const overrides = scoringFields.reduce((acc, field) => {
        const defaultValue = prev.defaults?.[field.key] ?? 0;
        const resolvedValue = nextResolved[field.key] ?? defaultValue;
        if (Math.abs(resolvedValue - defaultValue) > 0.0005) {
          acc[field.key] = Number(resolvedValue.toFixed(4));
        }
        return acc;
      }, {});

      return {
        defaults: prev.defaults,
        resolved: nextResolved,
        overrides: Object.keys(overrides).length ? overrides : null,
        source: Object.keys(overrides).length ? 'case-overrides' : 'global-defaults'
      };
    });
    setHasUnsavedChanges(true);
  }, []);

  const handleResetScoring = useCallback(() => {
    setScoringConfig(prev => {
      if (!prev) {
        return prev;
      }
      return {
        defaults: prev.defaults,
        resolved: { ...prev.defaults },
        overrides: null,
        source: 'global-defaults'
      };
    });
    setHasUnsavedChanges(true);
  }, []);

  const formatWeightPercent = useCallback((value) => (
    (Number(value || 0) * 100).toFixed(1)
  ), []);

  // PURE validation function: does NOT set state
  const getStepErrors = useCallback((stepIndex) => {
    const errors = [];
    switch (stepIndex) {
      case 0: {
        if (!caseData.title?.trim()) errors.push('Case title is required');
        if (!caseData.presentingComplaint?.trim()) errors.push('Presenting complaint is required');
        if (!caseData.specialty?.trim()) errors.push('Specialty is required');
        if (!caseData.patientName?.trim()) errors.push('Patient name is required');
        if (!caseData.gender) errors.push('Gender is required');
        if (!caseData.dateOfBirth || !/^\d{2}\/\d{2}\/\d{4}$/.test(caseData.dateOfBirth)) {
          errors.push('Date of birth is required (DD/MM/YYYY format)');
        }
        if (!caseData.age || Number(caseData.age) < 0 || Number(caseData.age) > 100) {
          errors.push('Valid age is required (0-100)');
        }
        if (!caseData.difficulty) errors.push('Difficulty level is required');
        break;
      }

      case 1: {
        if (!caseData.characterId) errors.push('Character selection is required');
        if (!caseData.openAIVoiceModel) errors.push('OpenAI voice model selection is required');
        if (!caseData.geminiVoiceModel) errors.push('Gemini voice model selection is required');
        break;
      }

      case 2: {
        if (!caseData.patientNarrative?.trim()) {
          errors.push('Patient narrative is required');
        }
        break;
      }

      case 3: {
        if (!caseData.sections || caseData.sections.length === 0) {
          errors.push('At least one section is required');
        } else {
          let hasAnyCompleteSection = false;
          const sectionIssues = [];

          caseData.sections.forEach((section) => {
            if (!section.items || section.items.length === 0) {
              sectionIssues.push(`Section "${section.title || 'Untitled'}" has no items`);
            } else {
              const incompleteItems = section.items.filter(item =>
                !item.title?.trim() || !item.markingCriteria?.trim() || !item.extendedInstructions?.trim()
              );
              if (incompleteItems.length === 0) {
                hasAnyCompleteSection = true;
              } else {
                sectionIssues.push(`Section "${section.title || 'Untitled'}" has ${incompleteItems.length} incomplete item(s)`);
              }
            }
          });

          if (!hasAnyCompleteSection) {
            errors.push('At least one section must have complete items (title, marking criteria, and extended instructions for all items)');
          }

          errors.push(...sectionIssues);
        }
        break;
      }

      case 4:
        // Questions are optional; no errors
        break;

      default:
        break;
    }
    return errors;
  }, [caseData]);

  // Action-time validation: sets errors, returns boolean
  const validateAndSetErrors = (stepIndex) => {
    const errors = getStepErrors(stepIndex);
    setValidationErrors(errors);
    return errors.length === 0;
  };

  const handleNext = () => {
    if (validateAndSetErrors(currentStep)) {
      setCurrentStep(prev => prev + 1);
      setError('');
      setValidationErrors([]); // Clear after moving forward if valid
    } else {
      setError('Please fix the issues below before continuing');
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => prev - 1);
    setError('');
    setValidationErrors([]);
  };

  const handleBackToCases = () => {
    if (hasUnsavedChanges) {
      const shouldLeave = window.confirm(
        'You have unsaved changes. Are you sure you want to leave? All changes will be lost.'
      );
      if (shouldLeave) {
        navigate('/cases');
      }
    } else {
      navigate('/cases');
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setValidationErrors([]);

      // Validate all required steps (excluding optional final step). Stop at first invalid step.
      for (const stepIndex of requiredStepIndexes) {
        if (!validateAndSetErrors(stepIndex)) {
          setError(`Please complete step ${stepIndex + 1} before saving`);
          setCurrentStep(stepIndex);
          setSaving(false);
          return;
        }
      }

      // Prepare data for submission
      const patientNarrative = (caseData.patientNarrative || '').trim();
      const examinationFindings = (caseData.examinationFindings || '').trim();
      const learningState = caseData.learning || {};
      const learningPayload = {
        summary: (learningState.summary || '').trim(),
        links: Array.isArray(learningState.links)
          ? learningState.links.filter((link) => link.label && link.url).map((link, index) => ({
            id: link.id || link._id || `link_${index}`,
            label: link.label.trim(),
            url: link.url.trim()
          }))
          : [],
        attachments: Array.isArray(learningState.attachments) ? learningState.attachments : []
      };

    const submitData = {
      title: caseData.title,
      presentingComplaint: caseData.presentingComplaint,
      specialty: caseData.specialty,
      tags: Array.isArray(caseData.tags) ? caseData.tags : [],
        patientDetails: {
          patientName: caseData.patientName,
          gender: caseData.gender,
          dateOfBirth: caseData.dateOfBirth ? displayToISO(caseData.dateOfBirth) : null
        },
        patientNarrative,
        examinationFindings,
        characterId: caseData.characterId,
        voiceModel: caseData.openAIVoiceModel,
        openAIVoiceModel: caseData.openAIVoiceModel,
        geminiVoiceModel: caseData.geminiVoiceModel,
        characterPersonality: caseData.characterPersonality,
        characterPersonalityCustom: caseData.characterPersonalityCustom,
        characterAccent: caseData.characterAccent,
        characterAccentCustom: caseData.characterAccentCustom,
        characterSpeechStyle: caseData.characterSpeechStyle,
        animationProfile: normalizeAnimationProfile(caseData.animationProfile),
        difficulty: caseData.difficulty,
        shortDescription: caseData.shortDescription,
        sections: caseData.sections.map(section => ({
          title: section.title,
          sectionType: section.sectionType,
          order: section.order,
          isCustom: section.isCustom,
          items: section.items.map(item => ({
            itemId: item.itemId,
            title: item.title,
            maxMarks: item.maxMarks,
            markingCriteria: item.markingCriteria,
            extendedInstructions: item.extendedInstructions,
            patientResponse: item.patientResponse,
            domainCodes: item.domainCodes || [],
            competencyCodes: item.competencyCodes || [],
            safetyCritical: Boolean(item.safetyCritical),
            omissionEligible: item.omissionEligible !== false,
            taxonomyVersion: item.taxonomyVersion || '1',
            order: item.order
          }))
        })),
        examinerQuestions: caseData.examinerQuestions.map(q => ({
          question: q.question,
          correctAnswer: q.correctAnswer,
          maxMarks: q.maxMarks,
          markingInstructions: q.markingInstructions,
          domainCodes: q.domainCodes || [],
          competencyCodes: q.competencyCodes || [],
          safetyCritical: Boolean(q.safetyCritical),
          taxonomyVersion: q.taxonomyVersion || '1',
          mediaFiles: q.mediaFiles || [],
          order: q.order
        })),
        learning: learningPayload
      };

      const visibilityState = caseData.visibility || {};
      const schedulingState = visibilityState.scheduling || {};
      const cohortState = visibilityState.cohort || {};

      submitData.visibility = {
        scheduling: {
          alwaysAvailable: schedulingState.alwaysAvailable !== false,
          startsAt: schedulingState.alwaysAvailable === false && schedulingState.startsAt
            ? localDateTimeToISO(schedulingState.startsAt)
            : null,
          endsAt: schedulingState.alwaysAvailable === false && schedulingState.endsAt
            ? localDateTimeToISO(schedulingState.endsAt)
            : null,
          timezone: detectedTimezone,
        },
        cohort: {
          allowAllGroups: cohortState.allowAllGroups !== false,
          allowedGroupIds: cohortState.allowAllGroups === false
            ? Array.from(new Set(
              (cohortState.allowedGroupIds || [])
                .map((value) => (value === null || value === undefined ? null : value.toString()))
                .filter(Boolean)
            ))
            : [],
          allowedYearLevels: Array.from(new Set(
            (cohortState.allowedYearLevels || [])
              .map((level) => (level === null || level === undefined
                ? null
                : level.toString().trim().toLowerCase()))
              .filter(Boolean)
          )),
        },
        accessNotes: visibilityState.accessNotes || '',
      };

      if (scoringConfig) {
        if (!scoringIsBalanced) {
          setError('Scoring weights must total 100% before saving.');
          setValidationErrors([`Scoring weights currently total ${(scoringTotal * 100).toFixed(1)}%.`]);
          setSaving(false);
          setCurrentStep(3);
          return;
        }

        if (scoringConfig.overrides) {
          submitData.scoringOverrides = scoringConfig.overrides;
        } else if (isEditing && initialScoringOverridesRef.current) {
          submitData.scoringOverrides = null;
        }
      }

      const method = isEditing ? 'put' : 'post';
      const url = isEditing ? `/cases/${id}` : '/cases';

      await api[method](url, submitData);
      initialScoringOverridesRef.current = scoringConfig?.overrides || null;

      setHasUnsavedChanges(false);

      navigate('/cases', {
        state: {
          message: `Case ${isEditing ? 'updated' : 'created'} successfully!`,
          type: 'success'
        }
      });

    } catch (err) {
      console.error('Error saving case:', err);

      let errorMessage = `Failed to ${isEditing ? 'update' : 'create'} case`;
      let errorDetails = [];

      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }
      
      if (err.response?.data?.errors && Array.isArray(err.response.data.errors)) {
        errorDetails = err.response.data.errors;
      } else if (err.response?.status === 400) {
        errorDetails = ['Invalid case data. Please check all required fields.'];
      } else if (err.response?.status === 403) {
        errorDetails = ['You do not have permission to save this case.'];
      } else if (err.response?.status === 409) {
        errorDetails = ['A case with this title already exists. Please use a different title.'];
      } else if (err.response?.status === 500) {
        errorDetails = ['Server error while saving case. Please try again.'];
      } else {
        errorDetails = [err.message || 'An unexpected error occurred.'];
      }

      setError(errorMessage);
      setValidationErrors(errorDetails);
    } finally {
      setSaving(false);
    }
  };

  const renderStepContent = (stepIndex) => {
    switch (stepIndex) {
      case 0:
        return (
          <PatientDetailsStep
            data={caseData}
            presets={presets}
            onUpdate={updateCaseData}
            validationErrors={validationErrors}
            allowNewSpecialty={allowNewSpecialty}
          />
        );
      case 1:
        return (
          <CharacterSelectionStep
            data={caseData}
            presets={presets}
            onUpdate={updateCaseData}
            validationErrors={validationErrors}
          />
        );
      case 2:
        return (
          <PatientNarrativeComposer
            data={caseData}
            onUpdate={updateCaseData}
            validationErrors={validationErrors}
          />
        );
      case 3:
        return (
          <CaseContentStep
            data={caseData}
            onUpdate={updateCaseData}
            validationErrors={validationErrors}
            scoringConfig={scoringConfig}
            scoringFields={scoringFields}
            scoringTotal={scoringTotal}
            scoringIsBalanced={scoringIsBalanced}
            onScoringChange={handleScoringWeightChange}
            onResetScoring={handleResetScoring}
            formatWeightPercent={formatWeightPercent}
            allowPublicTemplates={allowPublicTemplates}
          />
        );
      case 4:
        return (
          <QuestionsStep
            data={caseData}
            onUpdate={updateCaseData}
          />
        );
      case 5:
        return (
          <LearningStep
            data={caseData}
            onUpdate={updateCaseData}
            visibility={caseData.visibility}
            onVisibilityChange={(nextVisibility) => updateCaseData({ visibility: nextVisibility })}
            groups={presets.groups}
            provenance={caseData.provenance}
            showVisibilityControls={showVisibilityControls}
          />
        );
      default:
        return <div>Unknown step</div>;
    }
  };

  const completedSteps = useMemo(
    () => steps.map((_, index) => getStepErrors(index).length === 0),
    [getStepErrors]
  );

  const completedRequiredSteps = requiredStepIndexes.filter((index) => completedSteps[index]).length;
  const currentStepErrors = getStepErrors(currentStep);
  const progressPercent = Math.round((completedRequiredSteps / requiredStepIndexes.length) * 100);

  const canOpenStep = (stepIndex) => {
    if (stepIndex <= currentStep) {
      return true;
    }

    for (let index = 0; index < stepIndex; index += 1) {
      if (requiredStepIndexes.includes(index) && getStepErrors(index).length > 0) {
        return false;
      }
    }

    return true;
  };

  const handleStepSelect = (stepIndex) => {
    if (!canOpenStep(stepIndex)) {
      return;
    }

    setCurrentStep(stepIndex);
    setError('');
    setValidationErrors([]);
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={caseManagerPageSx}>
        <Box
          sx={{
            ...caseManagerSurfaceSx,
            py: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <CircularProgress sx={{ color: caseManagerPalette.heroBlueMid }} />
          <Typography sx={{ color: caseManagerPalette.inkSoft, fontFamily: publicBodyFontFamily }}>
            Loading case data...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={caseManagerPageSx}>
      <Box sx={{ ...caseManagerHeroSx, mb: 2 }}>
        <CaseManagerHeroSymbol />
        <Grid container spacing={2} alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid item xs={12} lg={8}>
            <Stack spacing={1.35}>
              <Chip
                icon={<TaskAltRoundedIcon sx={{ color: 'inherit !important' }} />}
                label={isEditing ? 'Case editor' : 'New case'}
                sx={{
                  alignSelf: 'flex-start',
                  bgcolor: alpha('#ffffff', 0.12),
                  color: caseManagerPalette.white,
                  borderRadius: '999px',
                  fontWeight: 800,
                  fontFamily: publicBodyFontFamily,
                }}
              />
              <Typography
                component="h1"
                sx={{
                  fontFamily: publicHeadingFontFamily,
                  fontSize: { xs: '1.85rem', md: '2.35rem' },
                  lineHeight: 1.08,
                  letterSpacing: 0,
                }}
              >
                {isEditing ? 'Edit case' : 'Create a new case'}
              </Typography>
              <Typography
                sx={{
                  maxWidth: 760,
                  color: alpha('#ffffff', 0.84),
                  fontSize: { xs: '0.94rem', md: '1rem' },
                  lineHeight: 1.55,
                  fontFamily: publicBodyFontFamily,
                }}
              >
                Build the patient, narrative, mark scheme, and release settings in a single guided flow.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap" useFlexGap>
                <Chip
                  label={`${completedRequiredSteps}/${requiredStepIndexes.length} required steps complete`}
                  sx={{
                    bgcolor: alpha('#ffffff', 0.12),
                    color: caseManagerPalette.white,
                    borderRadius: '999px',
                    fontWeight: 700,
                    fontFamily: publicBodyFontFamily,
                  }}
                />
                <Chip
                  label={hasUnsavedChanges ? 'Unsaved changes' : 'All changes saved locally'}
                  sx={{
                    bgcolor: alpha('#ffffff', 0.12),
                    color: caseManagerPalette.white,
                    borderRadius: '999px',
                    fontWeight: 700,
                    fontFamily: publicBodyFontFamily,
                  }}
                />
              </Stack>
            </Stack>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Stack spacing={1.2}>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={handleBackToCases}
                sx={{
                  ...publicSecondaryButtonSx,
                  minHeight: 42,
                  color: caseManagerPalette.white,
                  borderColor: alpha('#ffffff', 0.22),
                  bgcolor: alpha('#ffffff', 0.08),
                  '&:hover': {
                    borderColor: alpha('#ffffff', 0.34),
                    bgcolor: alpha('#ffffff', 0.14),
                  },
                }}
              >
                Back to cases
              </Button>
              <Button
                startIcon={<TipsAndUpdatesRoundedIcon />}
                onClick={() => setGuidelinesOpen(true)}
                sx={{
                  ...publicPrimaryButtonSx,
                  minHeight: 42,
                  bgcolor: caseManagerPalette.white,
                  color: caseManagerPalette.heroBlueDeep,
                  '&:hover': {
                    bgcolor: alpha('#ffffff', 0.92),
                  },
                }}
              >
                Case creation tips
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2.5, borderRadius: '20px' }}>
          {error}
        </Alert>
      )}

      {validationErrors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2.5, borderRadius: '20px' }}>
          <Typography sx={{ mb: 1, fontWeight: 800, fontFamily: publicBodyFontFamily }}>
            Please fix the following issues:
          </Typography>
          <ul style={{ margin: 0, paddingLeft: '20px' }}>
            {validationErrors.map((err, index) => (
              <li key={index}>{err}</li>
            ))}
          </ul>
        </Alert>
      )}

      <Paper sx={{ ...caseManagerSurfaceSx, p: { xs: 1.5, md: 1.8 }, mb: 2 }}>
        <Stack spacing={1.2}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ md: 'center' }}>
            <Box>
              <Typography sx={{ color: caseManagerPalette.ink, fontSize: '1rem', fontWeight: 800, fontFamily: publicHeadingFontFamily }}>
                Build progress
              </Typography>
              <Typography sx={{ mt: 0.35, color: caseManagerPalette.inkSoft, fontSize: '0.9rem', lineHeight: 1.4, fontFamily: publicBodyFontFamily }}>
                {completedRequiredSteps}/{requiredStepIndexes.length} required steps complete
              </Typography>
            </Box>
            <Typography sx={{ color: caseManagerPalette.heroBlueDeep, fontWeight: 800, fontSize: '1rem', fontFamily: publicBodyFontFamily }}>
              {progressPercent}%
            </Typography>
          </Stack>

          <LinearProgress
            variant="determinate"
            value={progressPercent}
            sx={{
              height: 6,
              borderRadius: '999px',
              bgcolor: alpha(caseManagerPalette.heroBlue, 0.12),
              '& .MuiLinearProgress-bar': {
                borderRadius: '999px',
                bgcolor: caseManagerPalette.heroBlueMid,
              },
            }}
          />

          <Box sx={{ display: 'flex', gap: 0.8, overflowX: 'auto', pb: 0.2 }}>
            {steps.map((label, index) => {
              const isCurrent = currentStep === index;
              const isComplete = completedSteps[index];
              const isClickable = canOpenStep(index);

              return (
                <Box
                  key={label}
                  onClick={() => handleStepSelect(index)}
                  sx={{
                    minWidth: { xs: 154, md: 166 },
                    p: 0.85,
                    borderRadius: '14px',
                    border: `1px solid ${alpha(caseManagerPalette.ink, isCurrent ? 0.14 : 0.08)}`,
                    bgcolor: isCurrent
                      ? caseManagerPalette.heroBlueDeep
                      : alpha(caseManagerPalette.heroBlueMist, 0.7),
                    color: isCurrent ? caseManagerPalette.white : caseManagerPalette.ink,
                    cursor: isClickable ? 'pointer' : 'not-allowed',
                    opacity: isClickable ? 1 : 0.55,
                    transition: 'background-color 160ms ease, transform 160ms ease',
                    '&:hover': isClickable ? {
                      transform: 'translateY(-1px)',
                      bgcolor: isCurrent
                        ? caseManagerPalette.heroBlueDeeper
                        : alpha(caseManagerPalette.heroBlueMid, 0.08),
                    } : null,
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box
                      sx={{
                        width: 26,
                        height: 26,
                        flexShrink: 0,
                        borderRadius: '10px',
                        display: 'grid',
                        placeItems: 'center',
                        bgcolor: isCurrent
                          ? alpha('#ffffff', 0.16)
                          : alpha(caseManagerPalette.heroBlueDeep, 0.08),
                        color: isCurrent ? caseManagerPalette.white : caseManagerPalette.heroBlueDeep,
                      }}
                    >
                      {isComplete ? (
                        <CheckCircleRoundedIcon sx={{ fontSize: 17 }} />
                      ) : (
                        <Typography sx={{ fontWeight: 800, fontSize: '0.84rem', fontFamily: publicBodyFontFamily }}>
                          {index + 1}
                        </Typography>
                      )}
                    </Box>
                    <Typography sx={{ minWidth: 0, fontSize: '0.9rem', fontWeight: 800, lineHeight: 1.25, fontFamily: publicBodyFontFamily }}>
                      {label}
                    </Typography>
                  </Stack>
                </Box>
              );
            })}
          </Box>
        </Stack>
      </Paper>

      <Grid container spacing={2} alignItems="stretch">
        <Grid item xs={12} xl={isEditing ? 9 : 12} display="flex" flexDirection="column" gap={2}>
          <Paper sx={{ ...caseManagerSurfaceSx, p: { xs: 1.8, md: 2.2 } }}>
            <Stack spacing={1.6}>
              <Box>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} justifyContent="space-between" alignItems={{ sm: 'center' }}>
                  <Box>
                    <Typography sx={{ color: caseManagerPalette.heroBlueMid, fontSize: '0.78rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', fontFamily: publicBodyFontFamily }}>
                      Step {currentStep + 1} of {steps.length}
                    </Typography>
                    <Typography sx={{ mt: 0.35, color: caseManagerPalette.ink, fontSize: { xs: '1.35rem', md: '1.55rem' }, lineHeight: 1.08, letterSpacing: 0, fontFamily: publicHeadingFontFamily }}>
                      {steps[currentStep]}
                    </Typography>
                  </Box>
                  <Chip
                    icon={completedSteps[currentStep] ? <CheckCircleRoundedIcon sx={{ color: 'inherit !important' }} /> : <RadioButtonUncheckedRoundedIcon sx={{ color: 'inherit !important' }} />}
                    label={completedSteps[currentStep] ? 'Step complete' : `${currentStepErrors.length} issue${currentStepErrors.length === 1 ? '' : 's'} to address`}
                    sx={{
                      alignSelf: { xs: 'flex-start', sm: 'center' },
                      bgcolor: alpha(caseManagerPalette.heroBlueMid, 0.10),
                      color: caseManagerPalette.heroBlueDeep,
                      borderRadius: '999px',
                      fontWeight: 800,
                      fontFamily: publicBodyFontFamily,
                    }}
                  />
                </Stack>
              </Box>

              <Divider sx={{ borderColor: alpha(caseManagerPalette.ink, 0.08) }} />

              {renderStepContent(currentStep)}
            </Stack>
          </Paper>
          {(caseData._newPresentingComplaint || caseData._newSpecialty) && (
            <Alert
              severity="info"
              sx={{
                borderRadius: '22px',
                border: `1px solid ${alpha(caseManagerPalette.heroBlueMid, 0.12)}`,
                bgcolor: alpha(caseManagerPalette.heroBlueMist, 0.82),
                '& .MuiAlert-message': {
                  width: '100%',
                },
              }}
            >
              <Typography sx={{ color: caseManagerPalette.ink, fontWeight: 800, fontFamily: publicBodyFontFamily }}>
                New preset items will be created on save.
              </Typography>
              {caseData._newPresentingComplaint && (
                <Typography sx={{ mt: 0.7, color: caseManagerPalette.inkSoft, fontFamily: publicBodyFontFamily }}>
                  Presenting complaint: "{caseData.presentingComplaint}"
                </Typography>
              )}
              {allowNewSpecialty && caseData._newSpecialty && (
                <Typography sx={{ mt: 0.35, color: caseManagerPalette.inkSoft, fontFamily: publicBodyFontFamily }}>
                  Specialty: "{caseData.specialty}"
                </Typography>
              )}
            </Alert>
          )}

          <Paper sx={{ ...caseManagerSurfaceSx, p: { xs: 1.6, md: 1.8 } }}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1.5}
              justifyContent="space-between"
              alignItems={{ md: 'center' }}
            >
              <Box>
                <Typography sx={{ color: caseManagerPalette.ink, fontSize: '1.05rem', fontWeight: 800, fontFamily: publicHeadingFontFamily }}>
                  {currentStep < steps.length - 1 ? 'Ready for the next step?' : 'Ready to save this case?'}
                </Typography>
                <Typography sx={{ mt: 0.35, color: caseManagerPalette.inkSoft, display: { xs: 'none', md: 'block' }, fontSize: '0.92rem', lineHeight: 1.5, fontFamily: publicBodyFontFamily }}>
                  {currentStep < steps.length - 1
                    ? 'Complete the current step, then continue through the remaining sections before publishing or sharing.'
                    : 'Saving will validate all required content, preserve any scoring overrides, and return you to the cases view.'}
                </Typography>
              </Box>

              <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={1.2} alignItems="stretch">
                <Button
                  onClick={handleBack}
                  disabled={currentStep === 0}
                  sx={publicSecondaryButtonSx}
                >
                  Back
                </Button>

                {currentStep < steps.length - 1 ? (
                  <Button
                    onClick={handleNext}
                    disabled={getStepErrors(currentStep).length > 0}
                    endIcon={<ArrowForwardRoundedIcon />}
                    sx={publicPrimaryButtonSx}
                  >
                    Next
                  </Button>
                ) : (
                  <Button
                    onClick={handleSave}
                    disabled={
                      saving ||
                      getStepErrors(0).length > 0 ||
                      getStepErrors(1).length > 0 ||
                      getStepErrors(2).length > 0 ||
                      getStepErrors(3).length > 0
                    }
                    sx={publicPrimaryButtonSx}
                  >
                    {saving ? 'Saving...' : (isEditing ? 'Update Case' : 'Create Case')}
                  </Button>
                )}
              </Stack>
            </Stack>
          </Paper>
        </Grid>

        {isEditing && (
          <Grid item xs={12} xl={3}>
            <Paper
              sx={{
                ...caseManagerSurfaceSx,
                p: { xs: 2.2, md: 2.5 },
                position: { xl: 'sticky' },
                top: { xl: 24 },
              }}
            >
              <CaseVersionTimeline
                caseId={id}
                caseTitle={caseData.title}
                onRestored={loadCase}
              />
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Guidelines dialog */}
      <CaseCreatorGuidelines
        open={guidelinesOpen}
        onClose={() => setGuidelinesOpen(false)}
      />
    </Container>
  );
};

export default CaseEditor;
